/**
 * ErrorBoundary — FSD class-component boundary.
 *
 * Catches render / lifecycle / hook errors from descendants and
 * renders a uniform `<ErrorState>` placeholder. Replaces the legacy
 * `_legacy/components/Shared/ErrorBoundary` which leaned on MUI
 * primitives and the dated "Oops, there seems to have been an error
 * loading data" copy.
 *
 * Default fallback offers a "Try again" button that calls
 * `window.location.reload()`. Callers can:
 *   - pass `fallback` (ReactNode) for a fully custom UI, or
 *   - pass `onReset` to override the default reload behaviour.
 *
 * Placement matters: outer instance (in AppProviders) guards
 * provider-level crashes; inner instance (inside AppShell content)
 * keeps the navigation chrome alive when only the page or its
 * lifecycle hooks (e.g. useBreadcrumbsSync) blow up.
 */
import { Component } from 'react';
import PropTypes from 'prop-types';
import ErrorState from '../ErrorState/ErrorState';
import isChunkLoadError from './isChunkLoadError';

// A stale bundle (a fresh deploy while the tab stayed open) makes lazy
// route chunks 404 and throw ChunkLoadError. We recover by hard-reloading
// once to pull the new bundle. The sessionStorage stamp debounces that
// reload so a chunk error that survives the reload (genuinely broken
// build) falls through to the error UI instead of looping forever.
const CHUNK_RELOAD_KEY = 'qhs:chunk-reload-at';
const CHUNK_RELOAD_DEBOUNCE_MS = 10000;

const justReloadedForChunk = () => {
  try {
    const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    return Date.now() - last < CHUNK_RELOAD_DEBOUNCE_MS;
  } catch {
    return false;
  }
};

const markChunkReload = () => {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable — the error UI will show instead */
  }
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, recoveringChunk: false };
  }

  static getDerivedStateFromError(error) {
    // When it's a recoverable stale-chunk error, flag recovery so render
    // shows a blank frame (no error flash) while componentDidCatch fires
    // the reload below.
    const recoveringChunk = isChunkLoadError(error) && !justReloadedForChunk();
    return { hasError: true, error, recoveringChunk };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error) && !justReloadedForChunk()) {
      markChunkReload();
      window.location.reload();
      return;
    }
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Reset the captured error when the boundary is told the
    // surrounding context has changed (typically location.pathname).
    // Without this, a sticky error from a previous route would
    // persist after the user navigates away — leaving the user
    // stuck on the ErrorState even though the URL has changed.
    const { resetKey } = this.props;
    if (this.state.hasError && resetKey !== prevProps.resetKey) {
      this.setState({ hasError: false, error: null, recoveringChunk: false });
    }
  }

  handleReset = () => {
    const { onReset } = this.props;
    if (onReset) {
      onReset();
      this.setState({ hasError: false, error: null });
      return;
    }
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    const { hasError, recoveringChunk } = this.state;
    const { fallback, fallbackTitle, fallbackMessage, fullHeight, children } = this.props;

    if (!hasError) return children;
    // Hard reload is in flight to pick up the new bundle — render nothing
    // so the error screen doesn't flash before the page reloads.
    if (recoveringChunk) return null;
    if (fallback) return fallback;

    return (
      <ErrorState
        title={fallbackTitle}
        message={fallbackMessage}
        onReload={this.handleReset}
        fullHeight={fullHeight}
      />
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
  fallbackTitle: PropTypes.node,
  fallbackMessage: PropTypes.node,
  fullHeight: PropTypes.bool,
  // Changing this value clears the captured error and re-renders
  // children. Pass `location.pathname` (from react-router) when the
  // boundary wraps page-level content so navigation away unsticks
  // a previous error.
  resetKey: PropTypes.any, // eslint-disable-line

  onReset: PropTypes.func,
};

export default ErrorBoundary;
